export type FiboSide = "buy" | "sell";

export interface FiboPlan {
  entryPrice: number;
  stopPrice: number;
}

export interface ResolvedFiboPricePlan extends FiboPlan {
  side: FiboSide;
}

export function tryResolveFiboPriceInput(
  entryPrice: number,
  stopPrice: number,
): ResolvedFiboPricePlan | null {
  if (
    !Number.isFinite(entryPrice) ||
    !Number.isFinite(stopPrice) ||
    entryPrice <= 0 ||
    stopPrice <= 0 ||
    entryPrice === stopPrice
  ) {
    return null;
  }
  return {
    entryPrice,
    stopPrice,
    side: entryPrice > stopPrice ? "buy" : "sell",
  };
}

export function resolveFiboPriceInput(
  entryPrice: number,
  stopPrice: number,
): ResolvedFiboPricePlan {
  if (!Number.isFinite(entryPrice) || !Number.isFinite(stopPrice) || entryPrice <= 0 || stopPrice <= 0) {
    throw new RangeError("The Fibo entry and stop prices must be positive finite numbers.");
  }
  if (entryPrice === stopPrice) {
    throw new RangeError("The Fibo entry and stop prices must be different.");
  }
  const resolved = tryResolveFiboPriceInput(entryPrice, stopPrice);
  if (resolved === null) throw new RangeError("The Fibo prices could not be resolved.");
  return resolved;
}

export function calculateFiboPlan(
  side: FiboSide,
  high: number,
  low: number,
  retracement: number,
  priceDigits = 2,
): FiboPlan {
  if (!Number.isFinite(high) || !Number.isFinite(low) || high <= 0 || low <= 0 || high <= low) {
    throw new RangeError("The Fibo high must be greater than the low.");
  }
  if (!Number.isFinite(retracement) || retracement <= 0 || retracement >= 1) {
    throw new RangeError("The Fibo retracement must be between 0 and 1.");
  }
  if (!Number.isInteger(priceDigits) || priceDigits < 0 || priceDigits > 8) {
    throw new RangeError("priceDigits must be an integer from 0 through 8.");
  }

  const range = high - low;
  const rawEntry = side === "sell"
    ? low + range * retracement
    : high - range * retracement;
  const round = (value: number): number => Number(value.toFixed(priceDigits));

  return {
    entryPrice: round(rawEntry),
    stopPrice: round(side === "sell" ? high : low),
  };
}

export function decimalPlaces(value: string): number {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return 0;
  const [coefficient = "", exponentText] = normalized.split("e", 2);
  const fractionLength = coefficient.includes(".")
    ? coefficient.length - coefficient.indexOf(".") - 1
    : 0;
  const exponent = exponentText === undefined ? 0 : Number(exponentText);
  return Number.isFinite(exponent) ? Math.max(0, fractionLength - exponent) : fractionLength;
}
